import { composeCallbackUrl, getFallbackRedirectUrl, isValidRedirectUrl } from './redirectUrl'
import { Route } from '../config/routes'
import { TrackingEvent } from '../model/monitoring'
import {
  authLogout,
  closeCheckout,
  persistor,
  useAppDispatch,
  useAuthentication,
  useCheckoutError,
  useCheckoutState,
  useCohort,
  useCountry,
  useRedirectUrl,
} from '../state'
import { useMerchantCallback } from './merchant'
import { getCurrentUserContextFromClient, useLDClient } from '@afterpaytouch/feature'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useTransactionMessage } from './transactionMessage'
import { useMonitoringWithEnduringEventProperties } from '../integrations/monitoring'
import { JourneyOutcome, mapTerminalErrorToJourney, USER_JOURNEY_NAMES, useCheckoutJourneyOwnership, useUserJourneyContext } from '../integrations/userJourney'
import { getExpiredJourneyTimeoutTerminationReason, getReplacedJourneyCompletion } from '../integrations/userJourney/timeout'
import { clearJourneyMarker, isMarkerFromCurrentPageLoad, readJourneyMarker } from '../integrations/userJourney/journeyMarker'
import { getCheckoutJourneyTags } from '../integrations/userJourney/checkoutTags'
import { stopDatadogSession } from '@afterpaytouch/integrations/datadog'
import { useRef } from 'react'
import { useRouter } from 'next/router'

export const useCloseCheckout: (options?: { redirectUrlOverride?: string }) => (sendEndTransactionMessage?: boolean) => Promise<void> = (options) => {
  const { initial, checkout, confirmed, referrer } = useCheckoutState()
  const { endTransaction } = useMerchantCallback()
  const { authenticated, persistent } = useAuthentication()
  const dispatch = useAppDispatch()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const defaultRedirectUrl = useRedirectUrl()
  const redirectUrl = options?.redirectUrlOverride || defaultRedirectUrl
  const LDClient = useLDClient()
  const closeCount = useRef(1)
  const endTransactionMessage = useTransactionMessage()
  const { addFriction, addFrustration, addTag, completeJourney, getActiveJourney, resumeJourney, startJourney } = useUserJourneyContext()
  const { claimRocketshipOwnership, owner, reportingEligibilityResolved, waitForOwnershipResolution, waitForReportingEligibility } =
    useCheckoutJourneyOwnership()
  const checkoutError = useCheckoutError()
  const cohort = useCohort()
  const country = useCountry()
  const { pathname } = useRouter()

  const reportError = (message: string, error, isRedirectError = false): void => {
    logEvent(TrackingEvent.CHECKOUT_FATAL_ERROR, {
      error: message,
      merchantName: checkout?.merchant.name,
      merchantId: checkout?.merchant.id,
    })

    const context = {
      'Merchant Data': { ...checkout?.merchant },
      'Order confirmed?': { confirmed: confirmed !== undefined, ...(confirmed ?? {}) },
      'Error details': { ...error },
    }

    capture.error(error, isRedirectError ? {} : context)
  }

  const clearSession = async (): Promise<void> => {
    dispatch(closeCheckout())
    if (persistor === null) {
      return
    }
    await persistor.flush()
  }

  const reportRedirectError = (message: string): void => reportError(message, new Error(message), true)

  const appendQueryArgs = (url: string, queryArgs: string): string => url + (url.includes('?') ? queryArgs : '?' + queryArgs)

  const getCallbackUrl = (queryArgs: string): string | undefined => {
    if (!redirectUrl) {
      reportRedirectError('No baseCallbackUrl to return to')
      const fallbackUrl = getFallbackRedirectUrl(referrer, reportRedirectError)
      return fallbackUrl === undefined ? undefined : appendQueryArgs(fallbackUrl, queryArgs)
    }
    const callbackUrl = composeCallbackUrl(redirectUrl, initial?.relativeCallbackUrl ?? '')
    const targetUrl = callbackUrl === undefined ? undefined : appendQueryArgs(callbackUrl, queryArgs)
    if (isValidRedirectUrl(targetUrl)) {
      return targetUrl
    }
    reportRedirectError('Invalid checkout callback URL')
    return getFallbackRedirectUrl(referrer, reportRedirectError)
  }

  const trackCloseCount = (): void => {
    logEvent(TrackingEvent.CLOSE_CALLED, {
      closeCount: closeCount.current,
    })
    closeCount.current += 1
  }

  return async (sendEndTransactionMessage = true) => {
    if (!reportingEligibilityResolved) {
      await waitForReportingEligibility()
    }

    if (confirmed === undefined) {
      const active = getActiveJourney()
      let hasCheckoutJourneyForClose = active?.name === USER_JOURNEY_NAMES.CHECKOUT
      let journeyExistingTags: readonly string[] = active?.name === USER_JOURNEY_NAMES.CHECKOUT ? active.tags : []
      let journeyTimeoutReason = active?.name === USER_JOURNEY_NAMES.CHECKOUT ? getExpiredJourneyTimeoutTerminationReason(active.created) : undefined
      if (active?.name !== USER_JOURNEY_NAMES.CHECKOUT) {
        let boundary = claimRocketshipOwnership()
        if (owner === 'negotiating' && boundary === undefined) {
          await waitForOwnershipResolution()
          const resumed = getActiveJourney()
          if (resumed?.name === USER_JOURNEY_NAMES.CHECKOUT) {
            hasCheckoutJourneyForClose = true
            journeyExistingTags = resumed.tags
            journeyTimeoutReason = getExpiredJourneyTimeoutTerminationReason(resumed.created)
          } else {
            boundary = claimRocketshipOwnership()
          }
        }
        if (boundary !== undefined) {
          hasCheckoutJourneyForClose = true
          const marker = readJourneyMarker()
          const returnsFromCashGrant = pathname === Route.CASH_AUTH || pathname === Route.APPLE_PAYLATER_CASH_AUTH
          const cashHandoffBelongsToCheckout =
            marker?.handoff !== true || (returnsFromCashGrant && (marker.checkoutToken === undefined || marker.checkoutToken === initial?.token))
          const resumesCurrentBoundary =
            marker?.handoff !== true &&
            marker?.journey.name === USER_JOURNEY_NAMES.CHECKOUT &&
            boundary.journeyId !== undefined &&
            marker.journey.id === boundary.journeyId
          const resumesCurrentHandoff =
            marker?.handoff === true &&
            (marker.checkoutToken === undefined || marker.checkoutToken === initial?.token) &&
            marker.journey.name === USER_JOURNEY_NAMES.CHECKOUT &&
            returnsFromCashGrant &&
            (boundary.journeyId === undefined || marker.journey.id === boundary.journeyId)
          const foreignCashHandoff = marker?.handoff === true && !cashHandoffBelongsToCheckout
          if (resumesCurrentBoundary || resumesCurrentHandoff) {
            resumeJourney(marker.journey, marker.esid)
            journeyExistingTags = marker.journey.tags
            journeyTimeoutReason = getExpiredJourneyTimeoutTerminationReason(marker.journey.created)
          } else {
            if (marker !== undefined && !foreignCashHandoff && (marker.handoff !== true || returnsFromCashGrant)) {
              resumeJourney(marker.journey, marker.esid)
              if (marker.handoff === true) {
                const replacement = getReplacedJourneyCompletion(marker.journey.created)
                if (replacement.outcome === JourneyOutcome.TERMINATED) {
                  completeJourney(marker.journey.name, replacement.outcome, replacement.terminationReason)
                } else {
                  completeJourney(marker.journey.name, replacement.outcome)
                }
              } else {
                completeJourney(marker.journey.name, isMarkerFromCurrentPageLoad(marker) ? JourneyOutcome.PROCESS_CRASHED : JourneyOutcome.PROCESS_KILLED)
              }
            } else if (foreignCashHandoff) {
              clearJourneyMarker()
            }
            startJourney(USER_JOURNEY_NAMES.CHECKOUT, boundary.startedAtEpochMs, boundary.journeyId)
            journeyTimeoutReason = getExpiredJourneyTimeoutTerminationReason(boundary.startedAtEpochMs)
          }
        }
      }
      if (hasCheckoutJourneyForClose) {
        for (const [key, value] of getCheckoutJourneyTags({
          merchantId: checkout?.merchant.id,
          country,
          cohort,
          existingTags: journeyExistingTags,
          initial,
        })) {
          addTag(USER_JOURNEY_NAMES.CHECKOUT, `${key}:${value}`)
        }
      }
      const isTerminalRoute = pathname === Route.TERMINAL_ERROR || pathname === Route.APPLE_PAYLATER_OH_NO
      if (journeyTimeoutReason !== undefined) {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.TERMINATED, journeyTimeoutReason)
      } else if (hasCheckoutJourneyForClose && isTerminalRoute) {
        const { outcome, signalType, signal } = mapTerminalErrorToJourney(checkoutError?.error, checkoutError?.errorCode)
        if (signalType === 'friction') {
          addFriction(USER_JOURNEY_NAMES.CHECKOUT, signal)
        } else {
          addFrustration(USER_JOURNEY_NAMES.CHECKOUT, signal)
        }
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, outcome)
      } else {
        completeJourney(USER_JOURNEY_NAMES.CHECKOUT, JourneyOutcome.USER_CANCELED)
      }
    }

    const queryArgs = confirmed?.callbackUrlQueryArgs ?? checkout?.merchant.callbackUrlQueryArgs ?? ''

    const logout = async (): Promise<void> => {
      if (authenticated && !persistent) {
        await dispatch(authLogout())
      }
    }

    const redirect = async (): Promise<void> => {
      const targetUrl = getCallbackUrl(queryArgs)
      trackCloseCount()
      await clearSession()
      if (isValidRedirectUrl(targetUrl)) {
        window.location.href = targetUrl
      }
    }

    const closePopup = async (): Promise<void> => {
      try {
        if (sendEndTransactionMessage) {
          await endTransaction(endTransactionMessage)
        }
      } catch (e) {
        reportError('No confirmation of end transaction', e)
      }
      trackCloseCount()
      await clearSession()
      if (!initial.isIframe) {
        window.close()
      }
    }

    const key = getCurrentUserContextFromClient(LDClient)?.key ?? null
    if (typeof LDClient !== 'undefined' && key !== null) {
      await LDClient.close()
    }

    await logout()

    stopDatadogSession()

    if (initial.isWindowed || initial.isIframe) {
      await closePopup()
    } else {
      await redirect()
    }
  }
}
